// Mirror of Shared/Calculators/DrywallCalculator.cs.
import type { CalcDef, ResultLine } from "../types.js";
import * as F from "../format.js";

const fields: CalcDef["fields"] = [
  { key: "roomWidthFt", kind: "number", default: 12, min: 1, max: 200 },
  { key: "roomLengthFt", kind: "number", default: 14, min: 1, max: 200 },
  { key: "ceilingHeightFt", kind: "number", default: 8, min: 6, max: 20 },
  {
    key: "ceiling",
    kind: "select",
    defaultChoice: "no",
    choices: [
      { value: "no", set: { includeCeiling: 0 } },
      { value: "yes", set: { includeCeiling: 1 } },
    ],
  },
  {
    key: "sheetSize",
    kind: "select",
    defaultChoice: "4x8",
    choices: [
      { value: "4x8", set: { sheetSqft: 32 } },
      { value: "4x12", set: { sheetSqft: 48 } },
      { value: "4x10", set: { sheetSqft: 40 } },
    ],
  },
  { key: "wastePct", kind: "number", default: 10, min: 0, max: 25 },
  { key: "pricePerSheet", kind: "number", default: 14, min: 0, max: 100 },
];

function compute(i: Record<string, number>): Record<string, number> {
  const w = F.get(i, "roomWidthFt");
  const l = F.get(i, "roomLengthFt");
  const h = F.get(i, "ceilingHeightFt");
  const includeCeiling = F.get(i, "includeCeiling");
  const sheetSqft = F.get(i, "sheetSqft");
  const waste = F.get(i, "wastePct");
  const price = F.get(i, "pricePerSheet");

  const wallArea = 2 * (w + l) * h;
  const ceilingArea = includeCeiling >= 1 ? w * l : 0;
  const total = wallArea + ceilingArea;
  const totalWithWaste = total * (1 + waste / 100);
  const sheets = sheetSqft > 0 ? F.ceilUnits(totalWithWaste / sheetSqft) : 0;
  const screws = sheets * 32;
  const compoundBuckets = F.ceilUnits(sheets / 12);
  const tapeFt = F.ceilUnits(total * 0.6);
  const cost = sheets * price;

  return {
    wallArea: F.round(wallArea, 2),
    ceilingArea: F.round(ceilingArea, 2),
    total: F.round(total, 2),
    sheets,
    screws,
    compoundBuckets,
    tapeFt,
    cost: F.round(cost, 2),
  };
}

function present(_i: Record<string, number>, r: Record<string, number>): ResultLine[] {
  const sheets = F.get(r, "sheets");
  const buckets = F.get(r, "compoundBuckets");
  return [
    { label: "Surface area", value: `${F.dec(F.get(r, "total"))} sq ft` },
    { label: "Sheets to buy", value: `${F.intStr(sheets)} ${F.plural(sheets, "sheet", "sheets")}`, highlight: true },
    { label: "Screws", value: `~${F.intStr(F.get(r, "screws"))}` },
    { label: "Joint compound", value: `${F.intStr(buckets)} ${F.plural(buckets, "bucket", "buckets")}` },
    { label: "Tape", value: `~${F.intStr(F.get(r, "tapeFt"))} ft` },
    { label: "Est. materials cost", value: F.usd(F.get(r, "cost")), sub: "rough — board only, excludes screws, mud, tape & labor" },
  ];
}

export const drywall: CalcDef = { slug: "drywall-calculator", fields, compute, present };
